import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, Button, ButtonGroup, Typography, TextField } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import PaletteIcon from "@mui/icons-material/Palette";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import DeleteIcon from "@mui/icons-material/Delete";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RestoreIcon from "@mui/icons-material/Restore";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PersonIcon from "@mui/icons-material/Person";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import SpellcheckIcon from "@mui/icons-material/Spellcheck";
import CloseIcon from "@mui/icons-material/Close";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getNodeByKey, $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND, TextNode, KEY_DOWN_COMMAND, COMMAND_PRIORITY_HIGH } from "lexical";
import { $patchStyleText } from "@lexical/selection";
import { $isWordNode, WordNode } from "./WordNode";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { ScriptLayerStore } from "../../../librettoEditor/store/ScriptLayerStore";
import { ApplicationStore } from "../../../librettoEditor/store/ApplicationStore";
import { CompositionStateStore } from "../../../librettoEditor/store/CompositionStateStore";
import { observer } from "mobx-react-lite";
import { eventMatchesPlatformChord, formatPlatformChord, getPlatform, type PlatformKeyChord } from "../../../librettoEditor/utils/shortcuts";
import { useTheme } from "@mui/material/styles";

/**
 * Edit mode for transcript selections:
 * - 'ignore': Remove from playback, show words as strikethrough
 * - 'delete': Remove from playback AND remove words from transcript
 * - 'gap': Replace with silent gap (maintain timeline position)
 * - 'transcript-only': Hide words from transcript, don't affect playback
 */
export type TranscriptEditMode = 'ignore' | 'delete' | 'gap' | 'transcript-only';

const editModeLabels: Record<TranscriptEditMode, { label: string; icon: React.ReactNode }> = {
  'ignore': { 
    label: 'Ignore Media',
    icon: <StrikethroughSIcon fontSize="small" />
  },
  'delete': { 
    label: 'Delete Media', 
    icon: <DeleteIcon fontSize="small" />
  },
  'gap': { 
    label: 'Replace with Gap', 
    icon: <VolumeOffIcon fontSize="small" />
  },
  'transcript-only': { 
    label: 'Remove from Transcript', 
    icon: <VisibilityOffIcon fontSize="small" />
  },
};

const editModeShortcuts: Record<TranscriptEditMode, PlatformKeyChord> = {
  ignore: {
    mac: { key: "Backspace", meta: true },
    windows: { key: "Backspace", ctrl: true },
  },
  delete: {
    mac: { key: "Backspace" },
    windows: { key: "Backspace" },
  },
  gap: {
    mac: { key: "Backspace", shift: true },
    windows: { key: "Backspace", shift: true },
  },
  "transcript-only": {
    mac: { key: "Backspace", alt: true },
    windows: { key: "Backspace", alt: true },
  },
};

type ToolbarProps = {
  onChangeSpeaker?: (startSec: number, endSec: number) => void;
  onDuplicateToNewComposition?: (startSec: number, endSec: number) => void;
  containerRef?: React.RefObject<HTMLElement>;
};

type SelectionSnapshot = {
  startSec: number;
  endSec: number;
  nodeKeys: string[];
};

const ShortcutPill = ({ label }: { label: string }) => (
  <Box
    component="kbd"
    sx={{
      ml: "auto",
      fontFamily: "Inter, sans-serif",
      fontSize: "11px",
      fontWeight: 600,
      color: "var(--app-text-muted)",
      backgroundColor: "var(--app-bg-subtle)",
      border: "1px solid var(--app-border-subtle)",
      borderBottom: "2px solid var(--app-border-muted)",
      borderRadius: "6px",
      px: 0.75,
      py: 0.25,
      lineHeight: "16px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      whiteSpace: "nowrap",
      userSelect: "none",
    }}
  >
    {label}
  </Box>
);

type CorrectionTarget = {
  wordKey: string;
  originalText: string;
  payload: any;
  anchorPos: { top: number; left: number };
};

const Toolbar: React.FC<ToolbarProps> = observer(({ onChangeSpeaker, onDuplicateToNewComposition, containerRef }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const surfaceBgColor = "var(--app-bg-surface)";
  const subtleBgColor = "var(--app-bg-subtle)";
  const borderSubtleColor = "var(--app-border-subtle)";
  const borderMutedColor = "var(--app-border-muted)";
  const textPrimaryColor = isDarkMode ? "var(--app-text-primary)" : "#111111";
  const iconPrimaryColor = isDarkMode ? "var(--app-icon-primary)" : "#555555";
  const mutedTextColor = "var(--app-text-muted)";
  const subtleTextColor = "var(--app-text-subtle)";
  const menuHoverColor = isDarkMode ? subtleBgColor : "#f5f5f6";
  const menuPaperBorder = isDarkMode ? `1px solid ${borderSubtleColor}` : "1px solid rgba(15, 15, 15, 0.06)";
  const primaryActionBg = isDarkMode ? "#2B6BFD" : "#111111";

  const [editor] = useLexicalComposerContext();
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: -9999, left: -9999 });
  const [colorAnchorEl, setColorAnchorEl] = useState<null | HTMLElement>(null);
  const [editAnchorEl, setEditAnchorEl] = useState<null | HTMLElement>(null);
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const [contextMenuAnchorPos, setContextMenuAnchorPos] = useState<{ top: number; left: number } | null>(null);
  const [contextMenuSelection, setContextMenuSelection] = useState<SelectionSnapshot | null>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [singleWordSelection, setSingleWordSelection] = useState<{ wordKey: string; originalText: string; payload: any } | null>(null);
  const [correctionTarget, setCorrectionTarget] = useState<CorrectionTarget | null>(null);
  const [correctionValue, setCorrectionValue] = useState("");
  const colorMenuOpen = Boolean(colorAnchorEl);
  const editMenuOpen = Boolean(editAnchorEl);
  const moreMenuOpen = Boolean(moreAnchorEl);
  const contextMenuOpen = Boolean(contextMenuAnchorPos);

  const platform = useMemo(() => getPlatform(), []);
  const isCorrecting = Boolean(correctionTarget);
  // NOTE: Menus must render in a portal (default) so they aren't clipped by the editor layout.
  const pointerDownRef = useRef(false);
  const keepOpenRef = useRef(false);
  
  // Check if we're in restore mode (a non-delete cut is selected)
  const selectedCut = ScriptLayerStore.selectedCut;
  const isRestoreMode =
    selectedCut !== null &&
    (selectedCut.transcriptMode === 'strikethrough' || selectedCut.transcriptMode === 'retake-strikethrough');
  
  /**
   * Restore the selected cut (bring audio back).
   */
  const handleRestore = () => {
    if (!selectedCut) return;
    
    console.log('[Toolbar] Restoring cut:', selectedCut.id);
    CompositionService.restoreScriptCut(selectedCut.id);
    ScriptLayerStore.setSelectedCut(null);
  };

  /**
   * Get the time range of the current selection.
   */
  const getSelectionTimeRange = (
    snapshotOverride?: Pick<SelectionSnapshot, "startSec" | "endSec"> | null
  ): { startSec: number; endSec: number } | null => {
    if (
      snapshotOverride &&
      Number.isFinite(snapshotOverride.startSec) &&
      Number.isFinite(snapshotOverride.endSec) &&
      snapshotOverride.startSec < snapshotOverride.endSec
    ) {
      return { startSec: snapshotOverride.startSec, endSec: snapshotOverride.endSec };
    }

    let result: { startSec: number; endSec: number } | null = null;
    
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection) || selection.isCollapsed()) return;
      
      const nodes = selection.getNodes().filter(n => $isWordNode(n)) as WordNode[];
      if (!nodes.length) return;
      
      const starts = nodes.map(n => n.__payload.startSec ?? 0);
      const ends = nodes.map(n => n.__payload.endSec ?? n.__payload.startSec ?? 0);
      const minStart = Math.min(...starts);
      const maxEnd = Math.max(...ends);
      
      if (minStart < maxEnd) {
        result = { startSec: minStart, endSec: maxEnd };
      }
    });
    
    return result;
  };

  /**
   * Handle "Play" action - seek to start of selection and play.
   */
  const handlePlay = () => {
    const range = getSelectionTimeRange();
    if (!range) return;
    
    console.log('[Toolbar] Playing from:', range.startSec);
    CompositionService.seek(range.startSec);
    CompositionService.play();
    setMoreAnchorEl(null);
  };

  const handlePlayFromSnapshot = (snapshotOverride: SelectionSnapshot | null) => {
    const range = getSelectionTimeRange(snapshotOverride);
    if (!range) return;

    console.log('[Toolbar] Playing from:', range.startSec);
    CompositionService.seek(range.startSec);
    CompositionService.play();
    setMoreAnchorEl(null);
  };

  const handleDuplicateToNewCompositionFromSnapshot = (snapshotOverride: SelectionSnapshot | null) => {
    if (!onDuplicateToNewComposition) return;

    // Prefer the already-mapped timeline range (keeps waveform + transcript in sync).
    const storeRange = ApplicationStore.selectedScriptLayerRange ?? null;
    if (storeRange && Number.isFinite(storeRange.start) && Number.isFinite(storeRange.end) && storeRange.end > storeRange.start) {
      onDuplicateToNewComposition(storeRange.start, storeRange.end);
      return;
    }

    const range = getSelectionTimeRange(snapshotOverride);
    if (!range) return;

    // Convert script clip-local time -> timeline time (accounts for cuts via segments + playbackRate).
    const EPS = 1e-3;
    const timelineStart =
      ScriptLayerStore.mapSourceTimeToCompositionTime(range.startSec) ??
      (range.startSec / (ScriptLayerStore.playbackRate || 1));
    const timelineEnd =
      ScriptLayerStore.mapSourceTimeToCompositionTime(Math.max(range.startSec, range.endSec - EPS)) ??
      (range.endSec / (ScriptLayerStore.playbackRate || 1));

    if (timelineEnd > timelineStart) {
      onDuplicateToNewComposition(timelineStart, timelineEnd);
    }
  };

  const handleDuplicateToNewComposition = () => {
    handleDuplicateToNewCompositionFromSnapshot(null);
    setMoreAnchorEl(null);
  };

  const handleCopySelection = () => {
    const range = getSelectionTimeRange();
    if (!range) return;
    try {
      const chunk = CompositionService.copyScriptChunk(range.startSec, range.endSec);
      if (chunk) {
        ApplicationStore.setScriptChunkClipboard(chunk);
      }
    } catch {}
    setMoreAnchorEl(null);
  };

  /**
   * Handle "Change Speaker" action - create a new speaker segment for the selected text.
   */
  const handleChangeSpeaker = () => {
    const range = getSelectionTimeRange();
    if (!range) return;
    
    console.log('[Toolbar] Change speaker for range:', range);
    
    // Call the parent callback to handle the speaker change
    if (onChangeSpeaker) {
      onChangeSpeaker(range.startSec, range.endSec);
    }
    
    setMoreAnchorEl(null);
  };

  const handleChangeSpeakerFromSnapshot = (snapshotOverride: SelectionSnapshot | null) => {
    const range = getSelectionTimeRange(snapshotOverride);
    if (!range) return;

    console.log('[Toolbar] Change speaker for range:', range);

    if (onChangeSpeaker) {
      onChangeSpeaker(range.startSec, range.endSec);
    }

    setMoreAnchorEl(null);
  };

  const handleAddScene = () => {
    const range = getSelectionTimeRange();
    if (range && Number.isFinite(range.startSec) && Number.isFinite(range.endSec) && range.endSec > range.startSec) {
      try {
        ApplicationStore.setPendingAddSceneFromSelectionContext({
          startSec: range.startSec,
          endSec: range.endSec,
        });
      } catch {}

      // Open the Properties sidebar + Layout picker for the scene containing the selection.
      let sceneIndex: number | null = null;
      try {
        const clip: any = CompositionService.getScriptLayerClip();
        if (clip && typeof clip.findSceneIndexForTime === "function") {
          const idx = Number(clip.findSceneIndexForTime(Math.max(0, Number(range.startSec) + 1e-6)));
          if (Number.isFinite(idx) && idx >= 0) sceneIndex = idx;
        }
      } catch {}

      window.dispatchEvent(
        new CustomEvent("openPropertiesSidebarLayout", {
          detail: {
            sceneIndex,
            // Preserve the current transcript selection; don't auto-select the full scene range.
            syncTranscriptSelection: false,
          },
        })
      );
      return;
    }

    // Fallback: when no selection, keep the old behavior (single split at playhead).
    const timelineTime = Number(CompositionStateStore.currentTime ?? 0);
    const mapped = ScriptLayerStore.mapTimelineTimeToSourceTime(timelineTime);
    if (mapped == null || !Number.isFinite(mapped)) return;
    const splitTimeSec = Math.max(0, Number(mapped));
    CompositionService.splitScriptLayer(splitTimeSec, true);
  };

  const resolveAddLayerStartSec = (): number => {
    const selectedRange = ApplicationStore.selectedScriptLayerRange;
    if (selectedRange && Number.isFinite(selectedRange.start)) {
      return Math.max(0, Number(selectedRange.start));
    }

    const textSelectionRange = getSelectionTimeRange();
    if (textSelectionRange) {
      const mappedStart =
        ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(textSelectionRange.startSec) ??
        ScriptLayerStore.mapSourceTimeToCompositionTime(textSelectionRange.startSec);
      if (mappedStart != null && Number.isFinite(mappedStart)) {
        return Math.max(0, Number(mappedStart));
      }
    }

    const timelineTime = Number(CompositionStateStore.currentTime ?? 0);
    return Number.isFinite(timelineTime) ? Math.max(0, timelineTime) : 0;
  };

  const handleAddLayer = () => {
    try {
      const range = getSelectionTimeRange();
      if (range) {
        // Selection-based Add Layer: create a scene spanning the selection and constrain the layer to that range.
        ApplicationStore.setPendingAddLayerFromSelectionContext({
          startSec: range.startSec,
          endSec: range.endSec,
        });
      } else {
        const startAtSec = resolveAddLayerStartSec();
        ApplicationStore.setPendingAddLayerContext({ startAtSec, constrainToSceneEnd: true });
      }
      window.dispatchEvent(new CustomEvent("openProjectSidebarFiles", { detail: { source: "text-toolbar" } }));
    } catch {}
  };

  const getContextSelection = useMemo(() => {
    return (): { hasSelection: boolean; snapshot: SelectionSnapshot | null } => {
      let hasSelection = false;
      let snapshot: SelectionSnapshot | null = null;

      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || selection.isCollapsed()) return;
        hasSelection = true;

        const nodes = selection.getNodes().filter((n) => $isWordNode(n)) as WordNode[];
        if (!nodes.length) return;

        const starts = nodes.map((n) => n.__payload.startSec ?? 0);
        const ends = nodes.map((n) => n.__payload.endSec ?? n.__payload.startSec ?? 0);
        const minStart = Math.min(...starts);
        const maxEnd = Math.max(...ends);
        if (minStart < maxEnd) {
          snapshot = {
            startSec: minStart,
            endSec: maxEnd,
            nodeKeys: nodes.map((n) => n.getKey()),
          };
        }
      });

      return { hasSelection, snapshot };
    };
  }, [editor]);

  const closeContextMenu = () => {
    setContextMenuAnchorPos(null);
    setContextMenuSelection(null);
  };

  useEffect(() => {
    const onContextMenu = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      // Scope to this editor instance (and specifically the transcript contentEditable).
      const containerEl = containerRef?.current;
      if (containerEl && !containerEl.contains(target)) return;
      if (!target.closest(".v2editor-scroll")) return;

      const { hasSelection, snapshot } = getContextSelection();
      if (!hasSelection) return;

      event.preventDefault();

      // Avoid multiple overlapping menus.
      setColorAnchorEl(null);
      setEditAnchorEl(null);
      setMoreAnchorEl(null);

      setContextMenuSelection(snapshot);
      setContextMenuAnchorPos({ top: event.clientY, left: event.clientX });
    };

    document.addEventListener("contextmenu", onContextMenu, { capture: true });
    return () => {
      document.removeEventListener("contextmenu", onContextMenu, { capture: true } as any);
    };
  }, [containerRef, getContextSelection]);

  const fmt = (format: "bold" | "italic" | "strikethrough") => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  useEffect(() => {
    // Track pointer interactions so we don't hide the toolbar mid-click (which prevents menus from opening).
    const onUp = () => {
      pointerDownRef.current = false;
      // Don't clear immediately: click fires after pointerup/mouseup. If we hide in between,
      // MUI menus never get a stable anchorEl and appear to "flash".
      window.setTimeout(() => {
        keepOpenRef.current = false;
      }, 0);
    };
    window.addEventListener("pointerup", onUp, { capture: true });
    window.addEventListener("pointercancel", onUp, { capture: true });
    window.addEventListener("mouseup", onUp, { capture: true });
    window.addEventListener("touchend", onUp, { capture: true });
    window.addEventListener("touchcancel", onUp, { capture: true });
    window.addEventListener("blur", onUp);
    return () => {
      window.removeEventListener("pointerup", onUp, { capture: true } as any);
      window.removeEventListener("pointercancel", onUp, { capture: true } as any);
      window.removeEventListener("mouseup", onUp, { capture: true } as any);
      window.removeEventListener("touchend", onUp, { capture: true } as any);
      window.removeEventListener("touchcancel", onUp, { capture: true } as any);
      window.removeEventListener("blur", onUp);
    };
  }, []);

  useEffect(() => {
    const updateFromSelection = () => {
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) && !selection.isCollapsed()) {
          const domSel = window.getSelection();
          if (domSel && domSel.rangeCount > 0) {
            const range = domSel.getRangeAt(0);
            const rect = range.getBoundingClientRect();

            const containerEl = containerRef?.current ?? (document.querySelector(".v2editor-scroll") as HTMLElement | null);
            const containerRect = containerEl?.getBoundingClientRect();

            const fallbackTop = Math.max(8, rect.top + window.scrollY - 44);
            const fallbackLeft = Math.max(8, rect.left + window.scrollX);

            // Prefer positioning relative to editor container (keeps toolbar inside the editor area).
            if (containerRect) {
              const toolbarW = toolbarRef.current?.offsetWidth ?? 320;
              const toolbarH = toolbarRef.current?.offsetHeight ?? 40;
              const padding = 8;

              const rawTop = rect.top - containerRect.top - 44;
              const rawLeft = rect.left - containerRect.left;

              const clampedTop = Math.min(
                Math.max(padding, rawTop),
                Math.max(padding, containerRect.height - toolbarH - padding),
              );
              const clampedLeft = Math.min(
                Math.max(padding, rawLeft),
                Math.max(padding, containerRect.width - toolbarW - padding),
              );

              setPos({ top: clampedTop, left: clampedLeft });
            } else {
              setPos({ top: fallbackTop, left: fallbackLeft });
            }
            setVisible(true);

            const nodes = selection.getNodes().filter((n) => $isWordNode(n)) as WordNode[];
            if (nodes.length === 1) {
              const node = nodes[0];
              setSingleWordSelection({ wordKey: node.getKey(), originalText: node.getTextContent(), payload: node.__payload });
            } else {
              setSingleWordSelection(null);
            }
            return;
          }
        }
        setSingleWordSelection(null);
        // Don't hide while the user is interacting with the toolbar or while menus are open.
        const menuOpen = colorMenuOpen || editMenuOpen || moreMenuOpen;
        if (!isCorrecting && !menuOpen && !pointerDownRef.current && !keepOpenRef.current) {
          setVisible(false);
        }
      });
    };
    const unregister = editor.registerUpdateListener(() => updateFromSelection());
    const onSelChange = () => updateFromSelection();
    document.addEventListener("selectionchange", onSelChange);
    updateFromSelection();
    return () => {
      unregister();
      document.removeEventListener("selectionchange", onSelChange);
    };
  }, [editor, containerRef, isCorrecting, colorMenuOpen, editMenuOpen, moreMenuOpen]);

  const colors: { name: string; value: string }[] = [
    { name: "Yellow", value: "#FFF59D" },
    { name: "Green", value: "#A5D6A7" },
    { name: "Blue", value: "#90CAF9" },
    { name: "Pink", value: "#F48FB1" },
    { name: "Orange", value: "#FFCC80" },
    { name: "Purple", value: "#CE93D8" },
  ];

  const sharpIconButtonSx = {
    width: 28,
    height: 28,
    borderRadius: "6px",
    color: textPrimaryColor,
    "& .MuiSvgIcon-root": {
      fontSize: 16,
    },
    "&:hover": {
      backgroundColor: subtleBgColor,
    },
  } as const;

  const inlineDividerSx = {
    width: "2px",
    height: "16px",
    backgroundColor: borderMutedColor,
    mx: "2px",
  } as const;

  const sharedMenuPaperSx = {
    padding: "6px",
    backgroundColor: surfaceBgColor,
    borderRadius: "10px",
    border: menuPaperBorder,
    boxShadow: "0 18px 40px rgba(0, 0, 0, 0.16)",
    "& .MuiList-root": {
      padding: 0,
      display: "flex",
      flexDirection: "column",
      gap: "2px",
    },
  } as const;

  const sharedMenuItemSx = {
    fontFamily: "Inter, sans-serif",
    fontSize: "13px",
    fontWeight: 400,
    color: textPrimaryColor,
    padding: "6px 10px",
    borderRadius: "8px",
    minHeight: "auto",
    "&:hover": {
      backgroundColor: menuHoverColor,
    },
  } as const;

  const applyHighlight = (color: string | null) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (color) {
          $patchStyleText(selection, { "background-color": color });
        } else {
          $patchStyleText(selection, { "background-color": "" });
        }
      }
    });
    setColorAnchorEl(null);
  };

  const openMenuOnMouseDown =
    (setAnchor: (el: HTMLElement | null) => void) => (e: React.MouseEvent<HTMLElement>) => {
      // Open on mousedown so selection doesn't collapse before click (which made menus "flash").
      e.preventDefault();
      setAnchor(e.currentTarget as HTMLElement);
    };

  /**
   * Apply an edit to the selected text range.
   * @param mode - The edit mode to apply
   */
  const applyEdit = (mode: TranscriptEditMode, snapshotOverride?: SelectionSnapshot | null) => {
    // Clear any selected cut when applying a new edit
    if (ScriptLayerStore.selectedCutId) {
      ScriptLayerStore.setSelectedCut(null);
    }

    let minStart = 0;
    let maxEnd = 0;
    let nodeKeys: string[] = [];

    if (snapshotOverride) {
      minStart = snapshotOverride.startSec;
      maxEnd = snapshotOverride.endSec;
      nodeKeys = snapshotOverride.nodeKeys;
    } else {
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || selection.isCollapsed()) return;

        const nodes = selection.getNodes().filter(n => $isWordNode(n)) as WordNode[];
        if (!nodes.length) return;

        // Use clip-local times (startSec/endSec) which are already adjusted for range/offset/playbackRate
        const starts = nodes.map(n => n.__payload.startSec ?? 0);
        const ends = nodes.map(n => n.__payload.endSec ?? n.__payload.startSec ?? 0);
        minStart = Math.min(...starts);
        maxEnd = Math.max(...ends);
        nodeKeys = nodes.map((n) => n.getKey());
      });
    }

    if (nodeKeys.length === 0) {
      setEditAnchorEl(null);
      return;
    }
    if (minStart >= maxEnd) {
      setEditAnchorEl(null);
      return;
    }

    // Optimistically update the transcript view immediately.
    editor.update(() => {
      const applyStrikethrough = () => {
        for (const key of nodeKeys) {
          const node = $getNodeByKey(key);
          if (!$isWordNode(node)) continue;
          const prev = node.getStyle() ?? "";
          if (/text-decoration\s*:\s*line-through/.test(prev)) continue;
          node.setStyle((prev ? prev + " " : "") + "text-decoration: line-through;");
        }
      };

      const removeWords = () => {
        for (const key of nodeKeys) {
          const node = $getNodeByKey(key);
          if (!$isWordNode(node)) continue;

          const next = node.getNextSibling();
          if (next instanceof TextNode && next.getTextContent() === " ") {
            next.remove();
          } else {
            const prev = node.getPreviousSibling();
            if (prev instanceof TextNode && prev.getTextContent() === " ") {
              prev.remove();
            }
          }

          node.remove();
        }
      };

      if (mode === "ignore") {
        applyStrikethrough();
      } else if (mode === "delete" || mode === "gap" || mode === "transcript-only") {
        removeWords();
      }
    }, { tag: 'historic' });

    console.log('[Toolbar] Applying edit:', { mode, minStart, maxEnd, nodeCount: nodeKeys.length });

    // Apply the appropriate edit based on mode
    switch (mode) {
      case 'ignore':
        // Remove from playback, show strikethrough
        CompositionService.cutScriptLayerRange(minStart, maxEnd, { transcriptMode: 'strikethrough' });
        break;
      case 'delete':
        // Remove from playback AND transcript (current behavior)
        CompositionService.cutScriptLayerRange(minStart, maxEnd, { transcriptMode: 'delete' });
        break;
      case 'gap':
        // Replace with silent gap (maintains timeline position)
        CompositionService.addScriptLayerGap(minStart, maxEnd);
        break;
      case 'transcript-only':
        // Hide from transcript only, don't affect playback
        CompositionService.hideTranscriptRange(minStart, maxEnd);
        break;
    }
    setEditAnchorEl(null);
  };

  const openCorrect = () => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection) || selection.isCollapsed()) return;
      const nodes = selection.getNodes().filter((n) => $isWordNode(n)) as WordNode[];
      if (nodes.length !== 1) return;
      const node = nodes[0];
      const text = node.getTextContent();
      setCorrectionValue(text);
      setCorrectionTarget({
        wordKey: node.getKey(),
        originalText: text,
        payload: node.__payload,
        anchorPos: { ...pos },
      });
    });
  };

  const closeCorrect = () => {
    setCorrectionTarget(null);
    setCorrectionValue("");
  };

  const applyCorrection = () => {
    if (!correctionTarget) return;
    const next = String(correctionValue ?? "").trim();
    if (!next || next === correctionTarget.originalText) {
      closeCorrect();
      return;
    }

    // Optimistically update the transcript view immediately.
    editor.update(() => {
      const node = $getNodeByKey(correctionTarget.wordKey);
      if ($isWordNode(node)) {
        node.setTextContent(next);
      }
    }, { tag: 'historic' });

    // Persist + undo/redo via script clip edits.
    CompositionService.correctTranscriptWord(correctionTarget.payload, next);

    closeCorrect();
  };

  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        // Only handle shortcuts inside the transcript editor, not inside toolbar popovers/menus.
        const target = event.target as HTMLElement | null;
        if (!target || !target.closest(".v2editor-scroll")) return false;

        // Let "/" be handled by SceneSplitPlugin.
        if (event.key === "/") return false;

        // Don't steal undo/redo from the global handler.
        const mod = platform === "mac" ? event.metaKey : event.ctrlKey;
        if (mod && (event.key === "z" || event.key === "y")) {
          event.preventDefault();
          return true;
        }

        // Change speaker shortcut: "@" (only when a word range is selected).
        if (event.key === "@") {
          const range = getSelectionTimeRange();
          if (!range) return false;
          event.preventDefault();
          handleChangeSpeaker();
          return true;
        }

        // Media edit shortcuts only apply when a word-range is selected.
        let hasRangeSelection = false;
        if (event.key === "Backspace") {
          editor.getEditorState().read(() => {
            const selection = $getSelection();
            hasRangeSelection = $isRangeSelection(selection) && !selection.isCollapsed();
          });
        }

        if (eventMatchesPlatformChord(event, editModeShortcuts.ignore, platform)) {
          if (!hasRangeSelection) return false;
          event.preventDefault();
          applyEdit("ignore");
          return true;
        }
        if (eventMatchesPlatformChord(event, editModeShortcuts.gap, platform)) {
          if (!hasRangeSelection) return false;
          event.preventDefault();
          applyEdit("gap");
          return true;
        }
        if (eventMatchesPlatformChord(event, editModeShortcuts["transcript-only"], platform)) {
          if (!hasRangeSelection) return false;
          event.preventDefault();
          applyEdit("transcript-only");
          return true;
        }
        if (eventMatchesPlatformChord(event, editModeShortcuts.delete, platform)) {
          if (!hasRangeSelection) return false;
          event.preventDefault();
          applyEdit("delete");
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor, platform]);

  return (
    <>
	    {/* Right-click context menu (only opens when a range selection exists) */}
	    <Menu
	      open={contextMenuOpen}
	      onClose={closeContextMenu}
	      anchorReference="anchorPosition"
	      anchorPosition={contextMenuAnchorPos ?? undefined}
	      anchorOrigin={{ vertical: "top", horizontal: "left" }}
	      transformOrigin={{ vertical: "top", horizontal: "left" }}
	      PaperProps={{
	        sx: {
	          minWidth: '220px',
	          ...sharedMenuPaperSx,
	        }
	      }}
	    >
		    <MenuItem
		        disabled={!contextMenuSelection}
		        onMouseDown={(e) => e.preventDefault()}
		        onClick={() => {
		          handleChangeSpeakerFromSnapshot(contextMenuSelection);
		          closeContextMenu();
		        }}
		        sx={sharedMenuItemSx}
		      >
		        <ListItemText
		          primary="Change speaker"
		          primaryTypographyProps={{
		            fontSize: '13px',
		            fontWeight: 400,
		            lineHeight: '18px',
		          }}
		        />
		        <ShortcutPill label="@" />
		      </MenuItem>

	      <MenuItem
	        disabled={!contextMenuSelection}
	        onMouseDown={(e) => e.preventDefault()}
	        onClick={() => {
	          handlePlayFromSnapshot(contextMenuSelection);
	          closeContextMenu();
	        }}
	        sx={sharedMenuItemSx}
	      >
	        <ListItemText
	          primary="Play"
	          primaryTypographyProps={{
	            fontSize: '13px',
	            fontWeight: 400,
	            lineHeight: '18px',
	          }}
	        />
	      </MenuItem>

	      <MenuItem
	        disabled={!contextMenuSelection}
	        onMouseDown={(e) => e.preventDefault()}
	        onClick={() => {
	          handleDuplicateToNewCompositionFromSnapshot(contextMenuSelection);
	          closeContextMenu();
	        }}
	        sx={sharedMenuItemSx}
	      >
	        <ListItemText
	          primary="Duplicate to new composition"
	          primaryTypographyProps={{
	            fontSize: '13px',
	            fontWeight: 400,
	            lineHeight: '18px',
	          }}
	        />
	      </MenuItem>

	      <MenuItem
	        disabled={!contextMenuSelection}
	        onMouseDown={(e) => e.preventDefault()}
	        onClick={() => {
	          applyEdit("delete", contextMenuSelection);
	          closeContextMenu();
	        }}
	        sx={sharedMenuItemSx}
	      >
	        <ListItemText
	          primary="Delete"
	          primaryTypographyProps={{
	            fontSize: '13px',
	            fontWeight: 400,
	            lineHeight: '18px',
	          }}
	        />
	        <ShortcutPill label={formatPlatformChord(editModeShortcuts.delete, platform)} />
	      </MenuItem>
	    </Menu>

	    {/* Correction popover (kept inside the editor container) */}
	    {correctionTarget ? (
	      <Box
	        sx={{
	          position: "absolute",
          top: `${Math.max(8, correctionTarget.anchorPos.top - 52)}px`,
          left: `${Math.max(8, correctionTarget.anchorPos.left)}px`,
          backgroundColor: surfaceBgColor,
          border: `1px solid ${borderSubtleColor}`,
          borderRadius: "10px",
          boxShadow: "0 18px 40px rgba(0,0,0,0.16)",
          padding: "8px",
          display: "flex",
          alignItems: "center",
          gap: 1,
          zIndex: 1001,
          maxWidth: "min(520px, calc(100% - 16px))",
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <TextField
          value={correctionValue}
          onChange={(e) => setCorrectionValue(e.target.value)}
          size="small"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              applyCorrection();
            } else if (e.key === "Escape") {
              e.preventDefault();
              closeCorrect();
            }
          }}
          sx={{
            flex: 1,
            minWidth: 160,
            "& .MuiInputBase-input": {
              fontFamily: "Inter, sans-serif",
              fontSize: "13px",
              color: textPrimaryColor,
            },
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={applyCorrection}
          sx={{
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 600,
            borderRadius: "8px",
            px: 1.5,
            minHeight: "35px",
            color: "#FFFFFF",
            backgroundColor: primaryActionBg,
            "&:hover": {
              backgroundColor: primaryActionBg,
              opacity: 0.9,
            },
          }}
        >
          Correct
        </Button>
        <IconButton size="small" onClick={closeCorrect} sx={{ color: mutedTextColor }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    ) : null}

    <Box
      ref={toolbarRef}
      sx={{
        position: containerRef?.current ? "absolute" : "fixed",
        top: `${pos.top}px`,
        left: `${pos.left}px`,
        transform: "translateY(-8px)",
        backgroundColor: surfaceBgColor,
        border: `1px solid ${borderSubtleColor}`,
        borderRadius: "8px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        padding: "4px",
        display: visible ? "flex" : "none",
        alignItems: "center",
        gap: 0.5,
        zIndex: 1000,
        maxWidth: "calc(100% - 16px)",
      }}
      onPointerDownCapture={() => {
        pointerDownRef.current = true;
        // Keep toolbar rendered through this click sequence even if DOM selection collapses.
        keepOpenRef.current = true;
      }}
      onMouseDownCapture={() => {
        pointerDownRef.current = true;
        keepOpenRef.current = true;
      }}
      onTouchStartCapture={() => {
        pointerDownRef.current = true;
        keepOpenRef.current = true;
      }}
      onMouseDown={(e) => {
        // Preserve selection while clicking toolbar controls (prevents selectionchange -> hide during click).
        e.preventDefault();
      }}
    >
      <Tooltip title="Add scene">
        <Button
          size="small"
          onClick={handleAddScene}
          sx={{
            minWidth: 104,
            height: 28,
            px: 1.25,
            borderRadius: "6px",
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 500,
            letterSpacing: "0.01em",
            whiteSpace: "nowrap",
            color: textPrimaryColor,
            "&:hover": {
              backgroundColor: subtleBgColor,
            },
          }}
        >
          Add Scene
        </Button>
      </Tooltip>

      <Tooltip title="Add Layer">
        <IconButton size="small" onClick={handleAddLayer} sx={sharpIconButtonSx}>
          <LayersOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Box sx={{ width: 2, height: 20, backgroundColor: borderMutedColor, mx: 0.5 }} />

      {/* Text formatting buttons */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          border: `1px solid ${borderSubtleColor}`,
          borderRadius: "8px",
          px: "2px",
          py: "1px",
        }}
      >
        <Tooltip title="Bold">
          <IconButton size="small" onClick={() => fmt("bold")} sx={sharpIconButtonSx}>
            <FormatBoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={inlineDividerSx} />
        <Tooltip title="Italic">
          <IconButton size="small" onClick={() => fmt("italic")} sx={sharpIconButtonSx}>
            <FormatItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={inlineDividerSx} />
        <Tooltip title="Highlight">
          <IconButton
            size="small"
            onMouseDown={openMenuOnMouseDown(setColorAnchorEl)}
            onClick={(e) => setColorAnchorEl(e.currentTarget)}
            sx={sharpIconButtonSx}
          >
            <PaletteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {singleWordSelection ? (
        <Tooltip title="Correct word">
          <IconButton size="small" onClick={openCorrect} sx={sharpIconButtonSx}>
            <SpellcheckIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      
      {/* Highlight color menu */}
      <Menu
        anchorEl={colorAnchorEl}
        open={colorMenuOpen}
        onClose={() => setColorAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            minWidth: '140px',
            ...sharedMenuPaperSx,
          }
        }}
      >
        {colors.map((c) => (
          <MenuItem 
            key={c.value} 
            onClick={() => applyHighlight(c.value)}
            sx={{ 
              ...sharedMenuItemSx,
              gap: '8px',
              '& .MuiListItemIcon-root': {
                minWidth: 'auto',
              },
            }}
          >
            <ListItemIcon>
              <span
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: c.value,
                }}
              />
            </ListItemIcon>
            <ListItemText 
              primary={c.name}
              primaryTypographyProps={{ 
                fontSize: '13px', 
                fontWeight: 400,
                lineHeight: '18px',
              }} 
            />
          </MenuItem>
        ))}
        <MenuItem 
          onClick={() => applyHighlight(null)}
          sx={{ 
            ...sharedMenuItemSx,
            gap: '8px',
            '& .MuiListItemIcon-root': {
              minWidth: 'auto',
            },
          }}
        >
          <ListItemIcon>
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: "transparent",
                border: `1px solid ${borderMutedColor}`,
              }}
            />
          </ListItemIcon>
          <ListItemText 
            primary="Clear highlight"
            primaryTypographyProps={{ 
              fontSize: '13px', 
              fontWeight: 400,
              lineHeight: '18px',
            }} 
          />
        </MenuItem>
      </Menu>

      {/* Divider */}
      <Box sx={{ width: 2, height: 20, backgroundColor: borderMutedColor, mx: 0.5 }} />

      {/* Edit split button: Restore (when cut selected) or Strikethrough + dropdown */}
      {isRestoreMode ? (
        <Tooltip title="Restore this section (bring audio back)">
          <Button
            variant="text"
            size="small"
            onClick={handleRestore}
            startIcon={<RestoreIcon fontSize="small" />}
            sx={{ 
              minWidth: 'auto',
              textTransform: 'none',
              fontSize: '12px',
              padding: '4px 8px',
              color: '#10B981', // Green to indicate restore action
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.18)' : 'rgba(16, 185, 129, 0.1)',
              }
            }}
          >
            Restore
          </Button>
        </Tooltip>
      ) : (
        <ButtonGroup 
          variant="text" 
          size="small" 
          sx={{ 
            '& .MuiButton-root': { 
              minWidth: 'auto',
              textTransform: 'none',
              fontSize: '12px',
              padding: '4px 8px',
              color: textPrimaryColor,
            }
          }}
        >
          <Tooltip title="Strikethrough (Ignore Media)">
            <Button
              onClick={() => applyEdit('ignore')}
              startIcon={<StrikethroughSIcon fontSize="small" />}
              sx={{ borderRight: `1px solid ${borderSubtleColor} !important` }}
            >
              Strikethrough
            </Button>
          </Tooltip>
          <Button
            size="small"
            onMouseDown={openMenuOnMouseDown(setEditAnchorEl)}
            onClick={(e) => setEditAnchorEl(e.currentTarget)}
            sx={{ px: 0.5 }}
          >
            <ArrowDropDownIcon fontSize="small" />
          </Button>
        </ButtonGroup>
      )}

      {/* Edit mode dropdown menu (only when not in restore mode) */}
      <Menu
        anchorEl={editAnchorEl}
        open={editMenuOpen && !isRestoreMode}
        onClose={() => setEditAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: '180px',
            ...sharedMenuPaperSx,
          }
        }}
      >
        {(Object.keys(editModeLabels) as TranscriptEditMode[]).map((mode) => {
          const { label, icon } = editModeLabels[mode];
          const shortcut = formatPlatformChord(editModeShortcuts[mode], platform);
          return (
            <MenuItem 
              key={mode} 
              onClick={() => applyEdit(mode)}
              sx={{ 
                ...sharedMenuItemSx,
                gap: '8px',
                '& .MuiListItemIcon-root': {
                  minWidth: 'auto',
                },
                '& svg': {
                  fontSize: 14,
                  color: iconPrimaryColor,
                },
              }}
            >
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText 
                primary={label}
                primaryTypographyProps={{ 
                  fontSize: '13px', 
                  fontWeight: 400,
                  lineHeight: '18px',
                }} 
              />
              <Typography
                variant="caption"
                sx={{
                  ml: "auto",
                  color: subtleTextColor,
                  fontSize: "11px",
                  fontWeight: 500,
                  pl: 2,
                }}
              >
                {shortcut}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Divider before more options */}
      <Box sx={{ width: 1, height: 20, backgroundColor: borderSubtleColor, mx: 0.5 }} />

      {/* Three dots menu for more options */}
      <Tooltip title="More options">
        <IconButton
          size="small"
          onMouseDown={openMenuOnMouseDown(setMoreAnchorEl)}
          onClick={(e) => setMoreAnchorEl(e.currentTarget)}
          sx={{
            color: textPrimaryColor,
            "&:hover": { backgroundColor: subtleBgColor },
          }}
        >
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* More options dropdown menu */}
      <Menu
        anchorEl={moreAnchorEl}
        open={moreMenuOpen}
        onClose={() => setMoreAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: '160px',
            ...sharedMenuPaperSx,
            '& .MuiList-root': { padding: 0 },
          }
        }}
      >
        <MenuItem 
          onClick={handlePlay} 
          sx={{ 
            ...sharedMenuItemSx,
            gap: '8px',
            '& .MuiListItemIcon-root': {
              minWidth: 'auto',
            },
          }}
        >
          <ListItemIcon sx={{ color: iconPrimaryColor }}>
            <PlayArrowIcon sx={{ fontSize: 14 }} />
          </ListItemIcon>
          <ListItemText 
            primary="Play" 
            primaryTypographyProps={{ 
              fontSize: '13px', 
              fontWeight: 400,
              lineHeight: '18px',
            }} 
          />
        </MenuItem>
        <MenuItem
          onClick={handleCopySelection}
          sx={{
            ...sharedMenuItemSx,
            gap: '8px',
            marginTop: '2px',
            '& .MuiListItemIcon-root': {
              minWidth: 'auto',
            },
          }}
        >
          <ListItemIcon sx={{ color: iconPrimaryColor }}>
            <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
          </ListItemIcon>
          <ListItemText
            primary="Copy"
            primaryTypographyProps={{
              fontSize: '13px',
              fontWeight: 400,
              lineHeight: '18px',
            }}
          />
        </MenuItem>
        <MenuItem 
          onClick={handleChangeSpeaker} 
          sx={{ 
            ...sharedMenuItemSx,
            gap: '8px',
            marginTop: '2px',
            '& .MuiListItemIcon-root': {
              minWidth: 'auto',
            },
          }}
        >
          <ListItemIcon sx={{ color: iconPrimaryColor }}>
            <PersonIcon sx={{ fontSize: 14 }} />
          </ListItemIcon>
          <ListItemText 
            primary="Change Speaker" 
            primaryTypographyProps={{ 
              fontSize: '13px', 
              fontWeight: 400,
              lineHeight: '18px',
            }} 
          />
        </MenuItem>
        <MenuItem
          disabled={!onDuplicateToNewComposition}
          onClick={handleDuplicateToNewComposition}
          sx={{
            ...sharedMenuItemSx,
            gap: '8px',
            marginTop: '2px',
            '& .MuiListItemIcon-root': {
              minWidth: 'auto',
            },
          }}
        >
          <ListItemIcon sx={{ color: iconPrimaryColor }}>
            <FileCopyOutlinedIcon sx={{ fontSize: 14 }} />
          </ListItemIcon>
          <ListItemText
            primary="Duplicate to new composition"
            primaryTypographyProps={{
              fontSize: '13px',
              fontWeight: 400,
              lineHeight: '18px',
            }}
          />
        </MenuItem>
      </Menu>
    </Box>
    </>
  );
});

export default Toolbar;
